import React, { useState } from 'react';
import { useDashboardTranslation } from '../i18n';
import { ImageOff } from 'lucide-react';
import type { WidgetProps } from '../types';
import { useWidgetFetch } from '../hooks/useWidgetFetch';
import { WidgetSkeleton } from '../components/WidgetSkeletons';
import { useReducedMotion } from '../utils/useReducedMotion';
import { readObject } from '../utils/widgetConfig';

interface ImagePayload {
  url: string;
  title?: string;
}

interface ImageWidgetConfig {
  /** Texto alternativo. Sin él el widget es inutilizable con lector de pantalla. */
  alt?: string;
  /** `contain` por defecto — ver abajo. */
  fit?: 'contain' | 'cover';
  caption?: string;
  /** Si está, la imagen navega. */
  link?: string;
}

/**
 * Widget de imagen.
 *
 * <b>No es un widget de datos: es contexto visual.</b> Logo de la compañía activa,
 * plano de bodega, organigrama, QR de un enlace operativo, banner de campaña. Por
 * eso se rige por reglas distintas al resto: no tiene periodo, no tiene delta y en
 * la mayoría de los casos no debe refrescarse solo.
 *
 * Tres cambios respecto de la versión anterior:
 *
 *  - <b>`contain` es el default, no `cover`.</b> Un plano o un organigrama recortado
 *    pierde justamente su información. `cover` queda para banners y fotos
 *    decorativas, donde recortar no destruye contenido.
 *  - <b>Se van el zoom en hover y el degradado negro encima.</b> Una imagen que se
 *    agranda al pasar el mouse y lleva una banda oscura con texto es decoración que
 *    tapa el contenido — sobre un plano, lo tapa literalmente.
 *  - <b>El `alt` sale de la configuración</b>, no del nombre del widget: "Ventas del
 *    periodo" no describe una imagen.
 */
const StaticImageWidget: React.FC<WidgetProps> = ({ definition, period }) => {
  const { t } = useDashboardTranslation();
  const reducedMotion = useReducedMotion();
  const [failed, setFailed] = useState(false);

  const { data, loading, error } = useWidgetFetch<ImagePayload>(
    definition.widget_id,
    period,
    definition.refresh_policy,
    definition.field_mapping?.rename
  );

  const config = readObject(definition.config_schema, 'image') as ImageWidgetConfig | undefined;
  const fit = config?.fit ?? 'contain';

  /**
   * Solo `http(s)`. La URL viene de la configuración del widget, que escribe un administrador del
   * catálogo, y de ahí se pinta para todos los que ven el tablero.
   *
   * React 19 bloquea `javascript:` por su cuenta —sustituye el atributo por una expresión que
   * lanza— pero el kit declara `react: "^18.0.0 || ^19.0.0"` en sus peers, y en React 18 eso es
   * solo un aviso de desarrollo. Dos líneas de lista blanca cierran el caso para los dos.
   */
  const enlaceSeguro = config?.link && /^https?:\/\//i.test(config.link) ? config.link : undefined;
  const alt = config?.alt ?? definition.descripcion ?? definition.nombre;
  const caption = config?.caption;

  if (loading) {
    // Reserva el área para que la grilla no salte cuando la imagen entra.
    return <WidgetSkeleton shape="image" reducedMotion={reducedMotion} />;
  }

  if (error || failed || !data?.url) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center gap-2 p-4 text-center"
        style={{ color: 'var(--dash-text-muted)' }}
        data-testid="image-error"
      >
        {/* Estado propio, nunca el icono roto del navegador. */}
        <ImageOff className="w-7 h-7 opacity-60" />
        <span className="text-[13px]">{t('dashboard.widget.imageUnavailable')}</span>
      </div>
    );
  }

  const image = (
    <img
      src={data.url}
      alt={alt}
      onError={() => setFailed(true)}
      className="w-full h-full"
      // La relación de aspecto se preserva SIEMPRE: ninguna configuración permite
      // deformar. `contain` deja ver el plano entero; `cover` recorta, y sólo se usa
      // donde recortar no quita información.
      style={{ objectFit: fit, borderRadius: 8, background: 'var(--dash-surface-page)' }}
    />
  );

  return (
    <div className="w-full h-full flex flex-col px-4 pb-4 gap-1.5">
      {/* Sin borde ni sombra propios: la tarjeta ya tiene borde, y una imagen
          enmarcada dentro de un marco es ruido. */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {enlaceSeguro ? (
          <a
            href={enlaceSeguro}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full h-full focus:outline-2 focus:outline-offset-2"
            style={{ outlineColor: 'var(--dash-accent)' }}
          >
            {image}
          </a>
        ) : (
          image
        )}
      </div>
      {caption && (
        <p
          className="text-[12px] truncate flex-shrink-0"
          style={{ color: 'var(--dash-text-muted)' }}
          title={caption}
        >
          {caption}
        </p>
      )}
    </div>
  );
};

export default StaticImageWidget;
